"use client";

import { useEffect, useRef } from "react";
import { ArrowRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { heroFileBadges } from "./homepage-data";
import { LandingDemoMenu } from "./LandingDemoMenu";

// A small pulsing "live" indicator dot (clay) for the window chrome.
function LiveDot() {
  return (
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--lb-clay))] opacity-70 motion-safe:animate-ping" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--lb-clay))]" />
    </span>
  );
}

// Hero centerpiece: the Paint Booth clip framed as a live browser window.
// The wide 1920x938 clip finally sits in a container built for its aspect ratio,
// and the chrome makes the "one browser tab" headline literal.
function HeroBrowserWindow() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // Honor reduced-motion: hold on the poster frame instead of autoplaying.
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (reduce.matches) {
        el.pause();
      } else {
        // play() rejects when autoplay is blocked — leave the poster showing.
        void el.play().catch(() => {});
      }
    };
    apply();
    reduce.addEventListener("change", apply);
    return () => reduce.removeEventListener("change", apply);
  }, []);

  return (
    <figure className="relative m-0 w-full overflow-hidden rounded-2xl border border-[hsl(var(--lb-line))] bg-[#0a0807] shadow-[0_34px_90px_-38px_rgba(0,0,0,0.85)] ring-1 ring-inset ring-[hsl(var(--lb-ink)/0.04)]">
      {/* Window chrome — decorative; the video below carries the accessible label. */}
      <div
        aria-hidden="true"
        className="flex items-center gap-3 border-b border-[hsl(var(--lb-line)/0.7)] bg-gradient-to-b from-[hsl(var(--lb-panel))] to-[hsl(var(--lb-bg-soft))] px-3.5 py-2.5"
      >
        <span className="flex shrink-0 gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lb-clay))]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lb-line))]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--lb-line))]" />
        </span>
        <span className="shrink-0 rounded-md border border-[hsl(var(--lb-line)/0.7)] bg-[hsl(var(--lb-ink)/0.06)] px-2.5 py-1 text-xs text-[hsl(var(--lb-ink))]">
          Paint Booth
        </span>
        <span className="ml-auto inline-flex shrink-0 items-center gap-2 whitespace-nowrap font-mono text-[0.66rem] uppercase tracking-[0.08em] text-[hsl(var(--lb-muted))]">
          <LiveDot />
          LIVE
        </span>
      </div>

      {/* Live viewport */}
      <div className="relative aspect-[1920/938] w-full">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          poster="/paint-booth-firetruck-poster.jpg"
          src="/paint-booth-firetruck.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-label="Paint Booth demo: a Los Santos Fire Department livery is edited in Photoshop and previewed live on the 3D firetruck model."
        />
        <figcaption
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--lb-line))] bg-black/65 px-2.5 py-1.5 font-mono text-[0.7rem] text-[hsl(var(--lb-ink))] backdrop-blur-sm"
        >
          <span className="h-2 w-2 rounded-[3px] bg-[hsl(var(--lb-clay))]" />
          livery.psd
        </figcaption>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--lb-line))] bg-black/65 px-2.5 py-1.5 font-mono text-[0.7rem] text-[hsl(var(--lb-ink))] backdrop-blur-sm"
        >
          <span className="h-2 w-2 rounded-[3px] bg-[hsl(var(--lb-amber))]" />
          3D preview ready
        </span>
      </div>
    </figure>
  );
}


// CTA row — kept separate from the hero body so the buttons stay consistent
// with the rest of the site.
function HeroCtaRow({ primaryLabel }: { primaryLabel: string }) {
  return (
    <>
      <Link
        to="/auth?mode=sign-up"
        className="inline-flex h-12 items-center justify-center rounded-xl bg-[hsl(var(--lb-clay))] px-6 text-base font-semibold text-white transition hover:-translate-y-0.5 sm:h-16 sm:px-9 sm:text-lg"
      >
        {primaryLabel}
        <ArrowRight className="ml-2 h-5 w-5" />
      </Link>
      <LandingDemoMenu
        label="Try a demo"
        className="h-12 rounded-xl border border-[hsl(var(--lb-ink)/0.2)] bg-[hsl(var(--lb-panel)/0.55)] px-5 text-base font-semibold text-[hsl(var(--lb-ink))] transition hover:border-[hsl(var(--lb-ink)/0.35)] hover:bg-[hsl(var(--lb-panel)/0.85)] sm:h-16 sm:px-8 sm:text-lg"
        leadingIcon={<Play className="mr-2 h-5 w-5" />}
      />
      <a
        href="https://discord.gg/theworkshop"
        target="_blank"
        rel="noreferrer"
        className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-xl border border-[hsl(var(--lb-ink)/0.2)] bg-[hsl(var(--lb-panel)/0.55)] px-5 text-base font-semibold text-[hsl(var(--lb-ink))] transition hover:border-[hsl(var(--lb-ink)/0.35)] hover:bg-[hsl(var(--lb-panel)/0.85)] sm:h-16 sm:px-8 sm:text-lg"
      >
        <svg className="mr-2 h-5 w-5" viewBox="0 0 127.14 96.36" fill="currentColor" aria-hidden="true">
          <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
        </svg>
        <span className="sm:hidden">Discord</span>
        <span className="hidden sm:inline">Join Our Discord</span>
      </a>
    </>
  );
}

// The "Cockpit" hero: punchier copy + the Paint Booth clip framed as a live
// browser window, making the "one browser tab" headline literal. Shipped to
// everyone as of 2026-08-31 — it was admin-gated while it was being built, and
// the previous intent-pills hero it replaced is in git history before that.
export function LandingHero() {
  return (
    <section className="relative overflow-x-hidden overflow-y-visible pb-8 pt-6 sm:pb-12 sm:pt-8 lg:pb-14">
      {/* Animated background glow orbs */}
      <div className="animate-glow-drift absolute left-[78%] top-[16%] h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--lb-clay)/0.14)] blur-[90px]" />
      <div className="animate-glow-drift-slow absolute left-[16%] top-[2%] h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--lb-amber)/0.09)] blur-[70px]" />

      <div className="lb-container relative z-10 grid gap-10 pt-2 sm:pt-4 lg:grid-cols-[1fr_1.02fr] lg:items-center">
        <div className="max-w-[46rem]">
          <h1
            className="lb-display landing-reveal text-[2.4rem] font-semibold leading-[1.04] text-[hsl(var(--lb-ink))] [text-wrap:balance] sm:text-[4rem] sm:leading-[0.95] lg:text-[4.5rem] xl:text-[5.25rem]"
            style={{ animationDelay: "0ms" }}
          >
            Ship every FiveM asset from one browser tab.
          </h1>
          <p
            className="landing-reveal mt-5 max-w-[42rem] text-base leading-[1.4] tracking-[-0.02em] text-[hsl(var(--lb-muted))] sm:mt-7 sm:text-[1.4rem] sm:leading-[1.35] sm:tracking-[-0.025em]"
            style={{ animationDelay: "110ms" }}
          >
            Retexture liveries, generate meta, scan resources, and package streaming-ready builds — one connected workspace. Zero installs, zero tool-switching.
          </p>
          <p
            className="landing-reveal mt-4 text-base tracking-[-0.02em] text-[hsl(var(--lb-subtle))] sm:text-lg"
            style={{ animationDelay: "200ms" }}
          >
            Free to start — no card required.
          </p>
          <div
            className="landing-reveal mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5 [&_a]:whitespace-nowrap [&_button]:whitespace-nowrap"
            style={{ animationDelay: "300ms" }}
          >
            <HeroCtaRow primaryLabel="Start building — free" />
          </div>
          <div
            className="landing-reveal mt-8 flex flex-wrap gap-3 sm:mt-10"
            style={{ animationDelay: "420ms" }}
          >
            {heroFileBadges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center rounded-full border border-[hsl(var(--lb-clay)/0.6)] bg-[hsl(var(--lb-bg-soft))] px-3.5 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--lb-ink))] shadow-[inset_0_1px_0_hsl(var(--lb-ink)/0.06),0_6px_18px_hsl(0_0%_0%/0.12)]"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="landing-reveal" style={{ animationDelay: "240ms" }}>
            <HeroBrowserWindow />
          </div>
        </div>
      </div>
    </section>
  );
}

